import {
  Autocomplete as MuiAutocomplete,
  CircularProgress as MuiCircularProgress,
  FormControl,
  MenuItem
} from '@mui/material';
import ExpandMore from '@mui/icons-material/ExpandMore';
import styled, { css } from 'styled-components';
import { computeSize, computeValue } from '../../utils/object-utilities';
import { heightToPixel, InputHeights } from '../../utils/consts';
import Icons from '../Icons';
import { Checkbox } from '../Checkbox';

const computeSelectPlaceholderTop = ({ $height, $minHeight }) => {
  const h = $height === InputHeights.DYNAMIC ? $minHeight : $height;
  const size = computeSize(h, heightToPixel, '35px');
  if (!size.endsWith('px')) return '-8px';
  const sizeNum = Number(size.replace('px', ''));
  if (sizeNum <= 24) return '-11px';
  if (sizeNum <= 32) return '-8px';
  if (sizeNum <= 36) return '-5px';
  return '0px';
};

const computeAutocompletePlaceholderTop = ({ $height, $minHeight }) => {
  const h = $height === InputHeights.DYNAMIC ? $minHeight : $height;
  const size = computeSize(h, heightToPixel, '35px');
  if (!size.endsWith('px')) return '-8px';
  const sizeNum = Number(size.replace('px', ''));
  if (sizeNum <= 24) return '-4px';
  if (sizeNum <= 32) return '-1px';
  if (sizeNum <= 36) return '1px';
  return '7px';
};

export const StyledWrapper = styled.div.attrs(() => ({
  className: 'dsc-select'
}))`
  width: ${({ $width }) => $width};
  &&& {
    font-family: var(--app-font) Averta, sans-serif;
    .MuiInputBase-root {
      &.Mui-disabled {
        background-color: var(--inputDisabledBgColor);
      }
    }

    .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline {
      border-color: ${({ $noBorder }) => ($noBorder ? 'transparent' : 'var(--select-focused-border-color)')};
      border-width: 1px;
    }
    .MuiOutlinedInput-root {
      border-radius: var(--select-border-radius, var(--input-border-radius));
      background-color: var(--select-bg-color);
      height: ${({ $height }) => computeSize($height, heightToPixel, '35px')};

      .MuiOutlinedInput-notchedOutline {
        border-radius: var(--select-border-radius, var(--input-border-radius));
        border-color: ${({ $noBorder }) => ($noBorder ? 'transparent' : 'var(--select-notched-border-color)')};
        border-width: 1px;
        legend {
          font-size: 12px;

          span {
            padding-left: unset;
          }
        }
      }
      .MuiOutlinedInput-input.Mui-disabled {
        color: var(--inputDisabledBaseColor);
        /* MUI paints disabled text through text-fill-color, which wins over color; fallback is MUI's own. */
        -webkit-text-fill-color: var(--select-disabled-text-fill-color, rgba(0, 0, 0, 0.38));
      }
    }
  }

  .MuiSelect-select {
    width: ${({ $width }) => $width};
    box-sizing: border-box;
    color: var(--select-text-color);
    font-size: 14px;
    padding: 0 14px;
  }
  .MuiInputLabel-outlined {
    font-size: 12px;
    top: ${computeSelectPlaceholderTop};
    color: var(--select-label-color);
  }

  .MuiFormHelperText-root {
    position: absolute;
    bottom: -18px; // same as TextField helper text position
    margin-top: unset;
    line-height: 1.42;
  }
`;
export const AutocompleteWrapper = styled.div.attrs(() => ({
  className: 'dsc-autocomplete'
}))`
  position: relative;
  font-family: var(--app-font) Averta, sans-serif;
  .MuiInputBase-root {
    &.Mui-disabled {
      background-color: var(--inputDisabledBgColor);
    }
  }
  .MuiAutocomplete-popper {
    .MuiAutocomplete-noOptions {
      font-size: 14px;
      color: var(--select-text-color);
    }

    .MuiAutocomplete-listbox {
      overflow: unset;
      max-height: ${({ $maxPopperHeight }) => $maxPopperHeight && computeValue($maxPopperHeight, 400, 'px')};
      .MuiAutocomplete-option {
        &.Mui-focused {
          background-color: var(--select-list-option-hover-bg);
        }
        &[aria-selected='true'] {
          background-color: var(--select-list-option-focused-bg);
        }
      }
    }
  }
  .MuiFormHelperText-root {
    position: absolute;
    bottom: -18px; // same as TextField helper text position
    color: var(--red);
    letter-spacing: 0.5px;
    line-height: 1.42;
  }
`;

export const AddTagAutocompleteWrapper = styled.div.attrs(() => ({
  className: 'dsc-autocomplete'
}))`
  position: relative;
  font-family: var(--app-font) Averta, sans-serif;
  &&&&& {
    .MuiInputBase-root {
      padding-right: 0px;
      .Mui-disabled {
        background-color: var(--inputDisabledBgColor);
      }
    }
    .MuiFormHelperText-root {
      top: 45px;
    }

    .MuiAutocomplete-noOptions {
      color: var(--select-text-color);
    }
  }
`;

export const TagWrapper = styled.div`
  display: flex;
  gap: 6px;
  width: ${({ $width }) => $width};
  flex-flow: wrap;
  margin-top: 10px;
  max-height: 400px;
  overflow-y: auto;
  padding-right: 6px;
  box-sizing: border-box;
`;

export const Autocomplete = styled(MuiAutocomplete)`
  width: ${({ $width }) => $width};
  ${({ $height }) =>
    $height !== InputHeights.DYNAMIC &&
    css`
      /* Give a try later
      .tagsContainer {
        flex-wrap: nowrap;
        max-width: calc(100% - 50px);
      }
      .tag-more {
        min-width: fit-content;
      }

      .dsc-tagRoot {
        max-width: 100%;
        overflow: hidden;
      }

      .tagContent div {
        min-width: 0;
      }

      .tagContent,
      .tagContent div {
        overflow: hidden;
        // The ellipses is not visible because content is flex. Moving to block (or inline) will defect the UI behavior
        text-overflow: ellipsis;
        white-space: nowrap;
      }
      */
    `};
  &&&& {
    font-family: var(--app-font) Averta, sans-serif;
    .MuiInputBase-root {
      height: ${({ $height }) => computeSize($height, heightToPixel, '35px')};
      min-height: ${({ $minHeight }) => $minHeight && computeSize($minHeight, heightToPixel, '35px')};
      display: flex;
      flex-wrap: nowrap;
      padding-right: ${({ clearIcon }) => (clearIcon ? '40px' : '20px')};
      padding-left: 10px;
    }
    .Mui-disabled {
      -webkit-text-fill-color: unset;
      // color: XYZ; TBD - pending design decision.
    }
  }
  /* Legacy keeps the input's own background; the var is only defined under .point-ds. */
  .point-ds &&& .MuiAutocomplete-input {
    background-color: var(--select-input-bg-color);
  }
  &&& {
    .MuiAutocomplete-input {
      min-width: 50px;
    }
    // outlined
    .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline {
      border-color: var(--select-focused-border-color);
      border-width: 1px;
    }

    .MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline {
      border-color: var(--red);
      border-width: var(--input-border-width);
    }

    .MuiFormHelperText-root {
      color: var(--red);
      letter-spacing: 0.5px;
      line-height: 1.42;
    }

    .MuiOutlinedInput-root {
      font-size: 14px;
      border-radius: var(--input-border-radius);
      background-color: var(--select-bg-color);
      color: var(--select-text-color);

      &.Mui-disabled {
        background-color: var(--inputDisabledBgColor);
      }

      .MuiOutlinedInput-notchedOutline {
        border-radius: var(--input-border-radius);
        border-color: var(--select-notched-border-color);
        border-width: 1px;
        legend {
          font-size: 12px;

          span {
            padding-left: unset;
          }
        }
      }
    }
    .MuiInputLabel-outlined {
      font-size: 12px;
      top: ${computeAutocompletePlaceholderTop};
      color: var(--select-label-color);
    }
    // end outlined
    .MuiInputBase-input {
      height: 21px;
      /** Hide Check box that is rendered as part of an option for multiple Select */
      .MuiCheckbox-root {
        display: none;
      }
    }
    .MuiInputLabel-root {
      font-family: var(--app-font) Averta, sans-serif;
    }
    .MuiInputLabel-root.Mui-focused {
      font-size: 12px;
      transform: translate(12px, -11px);
      color: var(--select-focused-label-color);
    }
    .MuiInputLabel-shrink {
      font-size: 12px;
      top: 0;
      transform: translate(12px, -11px);
    }
    .MuiSvgIcon-root {
      color: var(--select-svg-icon);
      top: 7px;
    }
    .MuiAutocomplete-endAdornment {
      padding-top: 3px;
    }
  }
`;

export const StyledMenuItem = styled(MenuItem)`
  &&& {
    font-size: 14px;
    display: flex;
    padding: 6px 16px;
    align-items: center;
    justify-content: flex-start;
    padding-left: ${({ $sub }) => $sub && '45px'};

    &.dsc-range-option {
      &:hover {
        background-color: var(--select-range-option-bg, var(--select-list-option-hover-bg));
      }
      &.Mui-selected,
      &.Mui-selected:hover {
        background-color: var(--select-range-option-bg, var(--select-list-option-focused-bg));
      }
    }
  }
`;

export const StyledExpandMore = styled(ExpandMore).attrs({ className: 'dsc-select-expand-more' })`
  && {
    font-size: 18px;
    cursor: pointer;
    ${({ $hasSelectedValue }) => !$hasSelectedValue && 'fill: var(--input-secondary-text-color)'};
  }
`;

export const TooltipWrapper = styled.div.attrs({
  className: 'tooltipWrapper'
})`
  margin-left: auto;
  pointer-events: all;
`;

export const StyledFormControl = styled(FormControl)`
  width: ${({ $width }) => $width};
  position: relative;
  && {
    .MuiInputBase-input {
      /** Hide Check box that is rendered as part of an option for multiple Select */
      .MuiCheckbox-root {
        display: none;
      }
    }
    .MuiInputLabel-root {
      font-family: var(--app-font) Averta, sans-serif;
    }
    .MuiInputLabel-root.Mui-focused {
      font-size: 12px;
      top: 0;
      transform: translate(11px, -9px);
      color: var(--select-focused-label-color);
    }
    .MuiInputLabel-shrink {
      font-size: 12px;
      top: 0;
      transform: translate(11px, -9px);
    }
    .MuiSvgIcon-root {
      color: var(--select-svg-icon);
    }
    ${TooltipWrapper} {
      display: none;
    }
  }
`;

export const Clear = styled(Icons.Close).attrs({
  fontSize: 14,
  color: 'var(--select-clear-icon)'
})`
  cursor: pointer;
`;

export const SelectClear = styled(Clear)`
  padding: 5px;
  right: 23px;
  position: absolute;
`;

export const StyledCheckbox = styled(Checkbox)`
  && {
    padding: 0;
    margin-right: 10px;
  }
`;

export const StyledOption = styled.div`
  display: flex;
  gap: 5px;
  flex: 1;
  align-items: center;
  overflow: hidden;
  color: var(--select-text-color);
  white-space: pre-wrap;
  word-break: break-word;

  ${({ $overrideCss }) =>
    $overrideCss &&
    css`
      ${$overrideCss}
    `}
`;

export const StyledTagOption = styled.div`
  display: flex;
  gap: 5px;
  flex: 1;
  align-items: center;
  overflow: hidden;
`;

export const MultiValueTooltipWrapper = styled.div`
  display: flex;
  flex-direction: column;
  padding: 5px;
  gap: 5px;
  max-height: 200px;
  overflow: auto;
`;

export const LoadingIcon = styled(Icons.InProgress)`
  position: absolute;
  top: -8px;
  right: -8px;
`;

export const ColoredCircle = styled.div`
  width: 9px;
  height: 9px;
  background-color: ${({ $color }) => $color};
  border-radius: var(--input-border-radius);
`;

export const MultiValueCommaSeparatedContainer = styled.div`
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
`;

export const POPPER_STYLE = {
  maxHeight: 400,
  borderRadius: 'var(--popper-radius)',
  boxShadow: '0px 2px 10px 0px #475B5E24',
  border: `1px solid var(--select-popup-list-border-color)`,
  color: 'var(--mainFontColor)',
  backgroundColor: `var(--select-popup-list-bg-color)`
};

export const Searching = styled.div`
  display: flex;
  align-items: center;
  color: var(--select-text-color);
  .loading {
    position: absolute;
    right: 10px;
    color: var(--input-accent-bg-color);
  }
`;
export const CircularProgress = styled(MuiCircularProgress)`
  && {
    color: inherit;
  }
`;
